import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Loader2, Building2, Truck, User, ShieldCheck } from 'lucide-react';
import { InvitationsTable } from '@/components/admin/InvitationsTable';
import { checkEmailAvailability } from '@/hooks/useInvitations';

type AccountType = 'shipper_company' | 'transporter_company' | 'driver' | 'admin';
type Mode = 'invite' | 'direct';

interface FormState {
  mode: Mode;
  email: string;
  first_name: string;
  last_name: string;
  phone: string;
  password: string;
  company_name: string;
  company_phone: string;
  founded_date: string;
  company_id: string; // pour driver
}

const emptyForm: FormState = {
  mode: 'invite',
  email: '',
  first_name: '',
  last_name: '',
  phone: '',
  password: '',
  company_name: '',
  company_phone: '',
  founded_date: '',
  company_id: '',
};

const CreateAccount = () => {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [tab, setTab] = useState<AccountType>('shipper_company');
  const [form, setForm] = useState<FormState>(emptyForm);
  const [busy, setBusy] = useState(false);

  const { data: transporters = [] } = useQuery({
    queryKey: ['admin-transporter-companies'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('companies')
        .select('id, name')
        .eq('type', 'transporter')
        .order('name');
      if (error) throw error;
      return data || [];
    },
  });

  const update = (patch: Partial<FormState>) => setForm((f) => ({ ...f, ...patch }));

  const handleTabChange = (v: string) => {
    setTab(v as AccountType);
    setForm(emptyForm);
  };

  const submit = async () => {
    // Validation
    if (!form.email || !form.first_name || !form.last_name || !form.phone) {
      toast.error(t('adminCreate.errors.missingUser'));
      return;
    }
    if (tab !== 'driver' && tab !== 'admin' && (!form.company_name || !form.company_phone || !form.founded_date)) {
      toast.error(t('adminCreate.errors.missingCompany'));
      return;
    }
    if (tab === 'driver' && !form.company_id) {
      toast.error(t('adminCreate.errors.pickCompany'));
      return;
    }
    if (form.mode === 'direct' && form.password.length < 8) {
      toast.error(t('adminCreate.errors.shortPassword'));
      return;
    }

    setBusy(true);
    try {
      // Contrôle préalable : adresse déjà utilisée ou invitation déjà en attente
      const availability = await checkEmailAvailability(form.email);

      if (availability.email_exists) {
        toast.error(
          t('invite.emailAlreadyUsed', {
            company: availability.company_name || '—',
            role: availability.role_code || '—',
          })
        );
        return;
      }

      if (availability.pending_invitation) {
        toast.error(
          t('invite.invitationAlreadyPending', {
            company: availability.pending_company || '—',
          })
        );
        return;
      }

      const { data, error } = await supabase.functions.invoke('admin-create-account', {
        body: {
          mode: form.mode,
          account_type: tab,
          email: form.email,
          first_name: form.first_name,
          last_name: form.last_name,
          phone: form.phone,
          password: form.mode === 'direct' ? form.password : undefined,
          company_name: tab === 'shipper_company' || tab === 'transporter_company' ? form.company_name : undefined,
          company_phone: tab === 'shipper_company' || tab === 'transporter_company' ? form.company_phone : undefined,
          founded_date: tab === 'shipper_company' || tab === 'transporter_company' ? form.founded_date : undefined,
          company_id: tab === 'driver' ? form.company_id : undefined,
        },
      });
      const fnError = (data as any)?.error;
      if (error || fnError) {
        const raw = String(fnError ?? error?.message ?? '');
        if (/already been registered|already registered|duplicate/i.test(raw)) {
          toast.error(t('adminCreate.errors.emailTaken'));
        } else {
          toast.error(t('adminCreate.errors.generic'));
        }
        return;
      }

      toast.success(
        form.mode === 'invite' ? t('adminCreate.success.invite') : t('adminCreate.success.direct')
      );
      setForm(emptyForm);
      qc.invalidateQueries({ queryKey: ['admin-transporter-companies'] });
      qc.invalidateQueries({ queryKey: ['admin-users'] });
      qc.invalidateQueries({ queryKey: ['invitations'] });
    } catch (e: any) {
      console.error('admin-create-account error:', e);
      toast.error(t('adminCreate.errors.generic'));
    } finally {
      setBusy(false);
    }
  };

  const inputCls =
    "border-[#DBE2EA] focus:border-[#0880AE] focus:ring-2 focus:ring-[#0880AE]/20 rounded-lg font-['Funnel_Sans']";
  const labelCls =
    "text-xs font-medium text-[#5A6B7A] uppercase tracking-wide font-['Funnel_Sans']";

  return (
    <div className="p-4 lg:p-6 max-w-4xl mx-auto">
      <h1 className="text-3xl font-semibold text-[#37699E] font-['Lexend_Deca'] mb-2">
        {t('adminCreate.title')}
      </h1>
      <p className="text-sm text-[#5A6B7A] mb-6 font-['Funnel_Sans']">
        {t('adminCreate.subtitle')}
      </p>

      <Tabs value={tab} onValueChange={handleTabChange}>
        <TabsList className="grid grid-cols-2 md:grid-cols-4 w-full bg-[#EBF4F8] rounded-lg p-1 h-auto">
          <TabsTrigger
            value="shipper_company"
            className="rounded-md data-[state=active]:bg-white data-[state=active]:text-[#37699E] data-[state=active]:shadow-sm flex items-center gap-2 py-3"
          >
            <Building2 className="h-4 w-4" />
            {t('adminCreate.tabs.shipper')}
          </TabsTrigger>
          <TabsTrigger
            value="transporter_company"
            className="rounded-md data-[state=active]:bg-white data-[state=active]:text-[#37699E] data-[state=active]:shadow-sm flex items-center gap-2 py-3"
          >
            <Truck className="h-4 w-4" />
            {t('adminCreate.tabs.transporter')}
          </TabsTrigger>
          <TabsTrigger
            value="driver"
            className="rounded-md data-[state=active]:bg-white data-[state=active]:text-[#37699E] data-[state=active]:shadow-sm flex items-center gap-2 py-3"
          >
            <User className="h-4 w-4" />
            {t('adminCreate.tabs.driver')}
          </TabsTrigger>
          <TabsTrigger
            value="admin"
            className="rounded-md data-[state=active]:bg-white data-[state=active]:text-[#37699E] data-[state=active]:shadow-sm flex items-center gap-2 py-3"
          >
            <ShieldCheck className="h-4 w-4" />
            {t('adminCreate.tabs.admin')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value={tab} className="mt-6">
          <div className="bg-white rounded-xl border border-[#DBE2EA] p-6 space-y-6">
            {/* Société */}
            {tab === 'admin' ? null : tab !== 'driver' ? (
              <section className="space-y-4">
                <h2 className="text-lg font-medium text-[#2D3748] font-['Lexend_Deca']">
                  {t('adminCreate.sections.company')}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className={labelCls}>{t('adminCreate.fields.companyName')} *</Label>
                    <Input
                      value={form.company_name}
                      onChange={(e) => update({ company_name: e.target.value })}
                      className={inputCls}
                      placeholder="ACME Logistics"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className={labelCls}>{t('adminCreate.fields.companyPhone')} *</Label>
                    <Input
                      value={form.company_phone}
                      onChange={(e) => update({ company_phone: e.target.value })}
                      className={inputCls}
                      placeholder="+33 1 23 45 67 89"
                    />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label className={labelCls}>{t('adminCreate.fields.foundedDate')} *</Label>
                    <Input
                      type="date"
                      max={new Date().toISOString().split('T')[0]}
                      value={form.founded_date}
                      onChange={(e) => update({ founded_date: e.target.value })}
                      className={inputCls}
                    />
                  </div>
                </div>
              </section>
            ) : (
              <section className="space-y-4">
                <h2 className="text-lg font-medium text-[#2D3748] font-['Lexend_Deca']">
                  {t('adminCreate.sections.attachToTransporter')}
                </h2>
                <div className="space-y-2">
                  <Label className={labelCls}>{t('adminCreate.fields.transporter')} *</Label>
                  <Select
                    value={form.company_id}
                    onValueChange={(v) => update({ company_id: v })}
                  >
                    <SelectTrigger className={inputCls}>
                      <SelectValue placeholder={t('adminCreate.fields.transporterPlaceholder')} />
                    </SelectTrigger>
                    <SelectContent>
                      {transporters.map((c: any) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </section>
            )}

            {/* Utilisateur */}
            <section className="space-y-4">
              <h2 className="text-lg font-medium text-[#2D3748] font-['Lexend_Deca']">
                {tab === 'driver'
                  ? t('adminCreate.sections.driverInfo')
                  : tab === 'admin'
                    ? t('adminCreate.sections.adminInfo')
                    : t('adminCreate.sections.ownerInfo')}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className={labelCls}>{t('adminCreate.fields.firstName')} *</Label>
                  <Input
                    value={form.first_name}
                    onChange={(e) => update({ first_name: e.target.value })}
                    className={inputCls}
                  />
                </div>
                <div className="space-y-2">
                  <Label className={labelCls}>{t('adminCreate.fields.lastName')} *</Label>
                  <Input
                    value={form.last_name}
                    onChange={(e) => update({ last_name: e.target.value })}
                    className={inputCls}
                  />
                </div>
                <div className="space-y-2">
                  <Label className={labelCls}>{t('adminCreate.fields.email')} *</Label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => update({ email: e.target.value })}
                    className={inputCls}
                  />
                </div>
                <div className="space-y-2">
                  <Label className={labelCls}>{t('adminCreate.fields.phone')} *</Label>
                  <Input
                    value={form.phone}
                    onChange={(e) => update({ phone: e.target.value })}
                    className={inputCls}
                    placeholder="+33 6 12 34 56 78"
                  />
                </div>
              </div>
            </section>

            {/* Mode */}
            <section className="space-y-4">
              <h2 className="text-lg font-medium text-[#2D3748] font-['Lexend_Deca']">
                {t('adminCreate.sections.activation')}
              </h2>
              <RadioGroup
                value={form.mode}
                onValueChange={(v) => update({ mode: v as Mode, password: '' })}
                className="space-y-3"
              >
                <label className="flex items-start gap-3 p-3 border border-[#DBE2EA] rounded-lg cursor-pointer hover:bg-[#EBF4F8] transition-colors">
                  <RadioGroupItem value="invite" className="mt-1" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-[#2D3748]">
                      {t('adminCreate.mode.invite.label')}
                    </p>
                    <p className="text-xs text-[#5A6B7A]">{t('adminCreate.mode.invite.desc')}</p>
                  </div>
                </label>
                <label className="flex items-start gap-3 p-3 border border-[#DBE2EA] rounded-lg cursor-pointer hover:bg-[#EBF4F8] transition-colors">
                  <RadioGroupItem value="direct" className="mt-1" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-[#2D3748]">
                      {t('adminCreate.mode.direct.label')}
                    </p>
                    <p className="text-xs text-[#5A6B7A]">{t('adminCreate.mode.direct.desc')}</p>
                  </div>
                </label>
              </RadioGroup>

              {form.mode === 'direct' && (
                <div className="space-y-2 max-w-md">
                  <Label className={labelCls}>{t('adminCreate.fields.password')} *</Label>
                  <Input
                    type="text"
                    value={form.password}
                    onChange={(e) => update({ password: e.target.value })}
                    className={inputCls}
                    placeholder={t('adminCreate.fields.passwordPlaceholder')}
                  />
                  <p className="text-xs text-[#5A6B7A]">{t('adminCreate.fields.passwordHint')}</p>
                </div>
              )}
            </section>

            <div className="flex justify-end pt-2">
              <Button
                onClick={submit}
                disabled={busy}
                className="bg-[#FFBF07] hover:bg-[#E5AB06] text-[#2D3748] font-semibold px-6 py-3 rounded-lg"
              >
                {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {form.mode === 'invite'
                  ? t('adminCreate.submit.invite')
                  : t('adminCreate.submit.direct')}
              </Button>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <InvitationsTable />
    </div>
  );
};

export default CreateAccount;
